/**
 * Presets API hooks (Wave 2.19 FE, TASK-065 + TASK-066a).
 *
 * Endpoints (Wave 2.18 BE-1, merged on main commit `806124e`):
 *   GET    /admin/presets?<filters>&cursor=&limit=        → PresetListResponse
 *   GET    /admin/presets/:id                             → PresetCardResponse
 *   POST   /admin/presets                                 → PresetCreateResponse
 *   PATCH  /admin/presets/:id                             → PresetPatchResponse
 *   POST   /admin/presets/:id/publish                     → PresetPublishResponse
 *   POST   /admin/presets/:id/archive                     → PresetPatchResponse
 *   POST   /admin/presets/:id/unarchive                   → PresetPatchResponse
 *   POST   /admin/presets/:id/duplicate                   → PresetCreateResponse
 *
 * RBAC: backend enforces `@AdminOrAbove()` (admin OR super_admin) on every
 * route. The FE route guard duplicates this check before any query fires so
 * selectors / client roles can't reach the page.
 *
 * Wire DTOs come exclusively from `@music-app/shared` — no local shims.
 *
 * Wave 2.19 BE-1 added the live track-count endpoint
 * (`GET /admin/presets/:id/track-count`) plus the version history
 * (`GET /admin/presets/:id/versions`); the FE hooks below consume them with no
 * shims. (The 5-track preview strip was removed — its hook no longer exists.)
 *
 * Wave 2.21 FE wire-contract gaps still open:
 *   - `POST /admin/presets/:id/rollback/:version_id` — TASK-063 not yet merged.
 *   - `GET  /admin/presets/:id/usage`                — usage endpoint absent.
 * Both surfaces render Alerts pointing at the follow-up tasks; the rollback
 * mutation hook is pre-wired so the BE merge needs zero FE changes.
 *
 * Invalidation strategy:
 *   - create:    invalidate `['presets-list']`.
 *   - update:    invalidate `['preset-card', id]` + `['presets-list']`.
 *   - publish:   invalidate `['preset-card', id]` + `['presets-list']`.
 *   - archive:   invalidate `['preset-card', id]` + `['presets-list']`.
 *   - unarchive: invalidate `['preset-card', id]` + `['presets-list']`.
 *   - duplicate: invalidate `['presets-list']`.
 *   - rollback:  invalidate `['preset-card', id]` + `['preset-versions', id]`
 *                + `['preset-track-count', id]` + `['presets-list']`.
 *
 * Stale-time policy (ADR-009 §1):
 *   - list   → `STALE_TIME.list`   (30 s)
 *   - single → `STALE_TIME.single` (10 s)
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import type {
  DayPartCode,
  LibraryType,
  PresetArchiveRequest,
  PresetAuthorType,
  PresetCardResponse,
  PresetCoverUploadResponse,
  PresetCreate,
  PresetCreateResponse,
  PresetDraftCreateResponse,
  PresetDraftPatchRequest,
  PresetDraftPatchResponse,
  PresetDuplicateRequest,
  PresetExcludesAdd,
  PresetListResponse,
  PresetPatchResponse,
  PresetPreviewCountByIdRequest,
  PresetPreviewCountByIdResponse,
  PresetPreviewCountRequest,
  PresetPreviewCountResponse,
  PresetPreviewSequenceResponse,
  PresetPriorityTrackAdd,
  PresetPublishRequest,
  PresetPublishResponse,
  PresetStatus,
  PresetTrackCountResponse,
  PresetTracksListQuery,
  PresetTracksListResponse,
  PresetUnarchiveRequest,
  PresetUpdate,
  PresetVersionListResponse,
  PresetExcludesListResponse,
  PresetPriorityListResponse,
  PresetUsageListResponse,
  TierAccess,
  VenueFormatTemplateTariff,
} from '@music-app/shared';
import { apiClient } from '../lib/api-client';
import { STALE_TIME } from '../lib/query-client';

export type {
  PresetListItem,
  PresetListResponse,
  PresetCardResponse,
  PresetCoverUploadResponse,
  PresetCreate,
  PresetCreateResponse,
  PresetUpdate,
  PresetPatchResponse,
  PresetPublishRequest,
  PresetPublishResponse,
  PresetArchiveRequest,
  PresetDuplicateRequest,
  PresetExcludesAdd,
  PresetPriorityTrackAdd,
  PresetVersionListResponse,
  PresetTrackCountResponse,
  PresetPreviewCountRequest,
  PresetPreviewCountResponse,
  PresetExcludesListItem,
  PresetExcludesListResponse,
  PresetPriorityListItem,
  PresetPriorityListResponse,
  PresetUsageListItem,
  PresetUsageListResponse,
} from '@music-app/shared';

// ---------------------------------------------------------------------------
// Wire filters mirror `presetListFilterSchema` in `@music-app/shared`. The
// FE-only filter type stays narrow — `status` accepts a single value (BE
// schema is single-valued); the "All" tab maps onto `undefined`.
// ---------------------------------------------------------------------------
export interface PresetsListFilters {
  q?: string;
  status?: PresetStatus;
  author_type?: PresetAuthorType;
  library?: LibraryType;
  tier?: TierAccess;
  account_id?: string;
  /** Showcase facets — design §14.1. `unmarked` = «нет ни одной категории». */
  showcase?: PresetShowcaseFilter;
  venue_format_id?: string;
  day_part?: DayPartCode;
  /**
   * Population of the category-template picker (design §19.16). The server
   * expands it into `TIER_WINDOW[tariff]`; the client never encodes the window
   * itself — `tier` above is a single tier and cannot express one.
   */
  template_tariff?: VenueFormatTemplateTariff;
}

export type PresetShowcaseFilter = 'marked' | 'unmarked';

export interface PresetsQueryParams {
  filters?: PresetsListFilters;
  cursor?: string;
  limit?: number;
}

/** Page size used both on the wire and to size the UI footer. Mirrors
 * `presetListFilterSchema.limit.default` in `@music-app/shared`. */
export const PRESETS_PAGE_SIZE = 50;
const PRESETS_ENDPOINT = '/admin/presets';
/** Mirrors `presetListFilterSchema.limit.max`. */
const MAX_LIMIT = 200;

function writeFilters(
  filters: PresetsListFilters | undefined,
  sp: URLSearchParams,
): void {
  if (!filters) return;
  if (filters.q) sp.set('q', filters.q);
  if (filters.status) sp.set('status', filters.status);
  if (filters.author_type) sp.set('author_type', filters.author_type);
  if (filters.library) sp.set('library', filters.library);
  if (filters.tier) sp.set('tier', filters.tier);
  if (filters.account_id) sp.set('account_id', filters.account_id);
  if (filters.showcase) sp.set('showcase', filters.showcase);
  if (filters.venue_format_id) {
    sp.set('venue_format_id', filters.venue_format_id);
  }
  if (filters.day_part) sp.set('day_part', filters.day_part);
  if (filters.template_tariff) {
    sp.set('template_tariff', filters.template_tariff);
  }
}

export function buildPresetsSearchParams(
  params: PresetsQueryParams,
): URLSearchParams {
  const sp = new URLSearchParams();
  writeFilters(params.filters, sp);
  const limit = Math.min(
    Math.max(1, params.limit ?? PRESETS_PAGE_SIZE),
    MAX_LIMIT,
  );
  sp.set('limit', String(limit));
  if (params.cursor) sp.set('cursor', params.cursor);
  return sp;
}

// ---------------------------------------------------------------------------
// Query keys — exported so callers can do precise invalidation if needed.
// ---------------------------------------------------------------------------
export const presetsListQueryKey = ['presets-list'] as const;
export const presetCardQueryKey = (id: string) => ['preset-card', id] as const;
/**
 * Query key for the live `/admin/presets/:id/track-count` endpoint (Wave 2.19
 * BE-1). The card mutations below invalidate it alongside the card row so the
 * Filter / Excludes / Priority panels see the recomputed match counter as soon
 * as the modal closes.
 */
export const presetTrackCountQueryKey = (id: string) =>
  ['preset-track-count', id] as const;

/**
 * Wave 2.58 FE-2 (TASK-282) — query key for the draft-filter live count.
 *
 * Unlike {@link presetTrackCountQueryKey} (which reads the persisted
 * `current_version.filter_rules`), this key scopes a `POST /admin/presets/
 * preview-count` call by the **debounced** ad-hoc filter body the user is
 * currently editing. The key embeds the request payload so React Query
 * caches each distinct filter independently — the editor can flip back and
 * forth without re-fetching.
 */
export const presetPreviewCountQueryKey = (
  payload: PresetPreviewCountRequest,
) => ['preset-preview-count', payload] as const;

/**
 * Wave 2.21 FE — query key for the versions list.
 */
export const presetVersionsQueryKey = (id: string) =>
  ['preset-versions', id] as const;

// ---------------------------------------------------------------------------
// Read hooks.
// ---------------------------------------------------------------------------

export function usePresetsListQuery(
  params: PresetsQueryParams,
  options?: { enabled?: boolean },
) {
  return useQuery<PresetListResponse>({
    queryKey: [...presetsListQueryKey, params],
    queryFn: () =>
      apiClient<PresetListResponse>(
        `${PRESETS_ENDPOINT}?${buildPresetsSearchParams(params).toString()}`,
      ),
    staleTime: STALE_TIME.list,
    placeholderData: (prev) => prev,
    enabled: options?.enabled ?? true,
  });
}

export function usePresetCardQuery(id: string | undefined) {
  return useQuery<PresetCardResponse>({
    queryKey: presetCardQueryKey(id ?? ''),
    queryFn: () => apiClient<PresetCardResponse>(`${PRESETS_ENDPOINT}/${id}`),
    staleTime: STALE_TIME.single,
    enabled: Boolean(id),
  });
}

/**
 * Wave 2.21 FE — versions list for the "Версии" tab. The endpoint exists in
 * Wave 2.19 BE-1 (`GET /admin/presets/:id/versions`) and returns
 * `presetVersionListResponseSchema` (each row carries `is_current` and a
 * nullable `track_count_at_publish` snapshot).
 */
export function usePresetVersionsQuery(id: string | undefined) {
  return useQuery<PresetVersionListResponse>({
    queryKey: presetVersionsQueryKey(id ?? ''),
    queryFn: () =>
      apiClient<PresetVersionListResponse>(`${PRESETS_ENDPOINT}/${id}/versions`),
    staleTime: STALE_TIME.single,
    enabled: Boolean(id),
  });
}

/**
 * Wave 2.21 FE — live matching-tracks count for the Filter tab badge and the
 * publish modal "Опубликовать ({count} треков)" CTA. `liveCounter` stale-time
 * (0 ms — ADR-009 §1) keeps the badge fresh while debouncing rapid refetches.
 */
export function usePresetTrackCountQuery(id: string | undefined) {
  return useQuery<PresetTrackCountResponse>({
    queryKey: presetTrackCountQueryKey(id ?? ''),
    queryFn: () =>
      apiClient<PresetTrackCountResponse>(`${PRESETS_ENDPOINT}/${id}/track-count`),
    staleTime: STALE_TIME.liveCounter,
    enabled: Boolean(id),
  });
}

/**
 * Wave 2.58 FE-2 (TASK-282) — live count for a draft (unsaved) preset filter.
 *
 * Calls `POST /admin/presets/preview-count` (Wave 2.57 BE-1) with the ad-hoc
 * filter body the user is editing. The endpoint accepts an unsaved
 * `presetFilterSchema` clause + library + optional `account_id` and returns
 * `{ matching_count, computed_at }` — see `presetPreviewCountRequestSchema` in
 * `@music-app/shared`.
 *
 * Debouncing is the caller's responsibility — pass the already-debounced
 * filter (e.g. via `useDebouncedValue(rawFilter, 300)` from `@mantine/hooks`)
 * so query keys don't churn on every keystroke. `liveCounter` stale-time
 * (0 ms — ADR-009 §1) keeps the chip fresh while still honouring React
 * Query's dedupe window for back-to-back identical bodies.
 *
 * Pass `enabled: false` from the call site to short-circuit the query while
 * the filter is invalid (e.g. `bpm_min > bpm_max`) — the UI chip then shows a
 * neutral placeholder instead of firing a 422.
 */
export function usePresetPreviewCountQuery(
  payload: PresetPreviewCountRequest | null,
  options?: { enabled?: boolean },
) {
  const enabled = (options?.enabled ?? true) && payload !== null;
  return useQuery<PresetPreviewCountResponse>({
    queryKey: presetPreviewCountQueryKey(
      payload ?? { filter: {}, library: 'royalty_free' },
    ),
    queryFn: () =>
      apiClient<PresetPreviewCountResponse>(
        `${PRESETS_ENDPOINT}/preview-count`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        },
      ),
    staleTime: STALE_TIME.liveCounter,
    enabled,
  });
}

/**
 * Wave 2.69 BE — preset "Использование" tab (TASK-066c-followup-usage).
 *
 * `GET /admin/presets/:id/usage?limit&offset` — paginated list of venues that
 * reference this preset via `customers.schedule_entries`. Each row carries
 * `active_slots_count` (number of distinct schedule slots) and `last_played_at`
 * (`MAX(play_events.started_at)` for the preset in this venue, NULL when the
 * preset has been scheduled but never actually played).
 *
 * Mirrors the offset-paginated envelope used by the excludes / priority list
 * endpoints (Wave 2.68 BE Goal 4).
 */
export interface PresetUsageQuery {
  limit: number;
  offset: number;
}

export const presetUsageListQueryKey = (
  id: string,
  query: PresetUsageQuery,
) => ['preset-usage', id, query] as const;

export function usePresetUsageQuery(
  id: string | undefined,
  query: PresetUsageQuery,
  options?: { enabled?: boolean },
) {
  const enabled = (options?.enabled ?? true) && Boolean(id);
  return useQuery<PresetUsageListResponse>({
    queryKey: presetUsageListQueryKey(id ?? '', query),
    queryFn: () => {
      const sp = new URLSearchParams();
      sp.set('limit', String(query.limit));
      sp.set('offset', String(query.offset));
      return apiClient<PresetUsageListResponse>(
        `${PRESETS_ENDPOINT}/${id}/usage?${sp.toString()}`,
      );
    },
    staleTime: STALE_TIME.list,
    enabled,
    placeholderData: (prev) => prev,
  });
}

// ---------------------------------------------------------------------------
// Mutation hooks. Each one awaits invalidation BEFORE the modal closes so the
// next user interaction sees fresh data (Wave 2.13 FE M-1 lesson).
// ---------------------------------------------------------------------------

export function useCreatePresetMutation(): UseMutationResult<
  PresetCreateResponse,
  Error,
  PresetCreate
> {
  const queryClient = useQueryClient();
  return useMutation<PresetCreateResponse, Error, PresetCreate>({
    mutationFn: (dto) =>
      apiClient<PresetCreateResponse>(PRESETS_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify(dto),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: presetsListQueryKey });
    },
  });
}

export interface UpdatePresetInput extends PresetUpdate {
  id: string;
}

export function useUpdatePresetMutation(): UseMutationResult<
  PresetPatchResponse,
  Error,
  UpdatePresetInput
> {
  const queryClient = useQueryClient();
  return useMutation<PresetPatchResponse, Error, UpdatePresetInput>({
    mutationFn: ({ id, ...patch }) =>
      apiClient<PresetPatchResponse>(`${PRESETS_ENDPOINT}/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: async (data) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: presetCardQueryKey(data.id) }),
        queryClient.invalidateQueries({ queryKey: presetsListQueryKey }),
      ]);
    },
  });
}

export interface UploadPresetCoverInput {
  id: string;
  file: File;
}

/**
 * `POST /admin/presets/:id/cover` — one-shot multipart upload of the preset
 * cover (TASK-408). The single field is named `file`; `apiClient` leaves the
 * `Content-Type` unset for a `FormData` body so the browser adds the multipart
 * boundary itself. On success the card query is invalidated so the freshly
 * presigned `cover_url` re-renders (the response also carries it), and the list
 * refreshes since the upload bumps `updated_at`.
 */
export function useUploadPresetCoverMutation(): UseMutationResult<
  PresetCoverUploadResponse,
  Error,
  UploadPresetCoverInput
> {
  const queryClient = useQueryClient();
  return useMutation<PresetCoverUploadResponse, Error, UploadPresetCoverInput>({
    mutationFn: ({ id, file }) => {
      const formData = new FormData();
      formData.append('file', file);
      return apiClient<PresetCoverUploadResponse>(
        `${PRESETS_ENDPOINT}/${id}/cover`,
        { method: 'POST', body: formData },
      );
    },
    onSuccess: async (data) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: presetCardQueryKey(data.preset_id),
        }),
        queryClient.invalidateQueries({ queryKey: presetsListQueryKey }),
      ]);
    },
  });
}

export interface PublishPresetInput extends PresetPublishRequest {
  id: string;
}

export function usePublishPresetMutation(): UseMutationResult<
  PresetPublishResponse,
  Error,
  PublishPresetInput
> {
  const queryClient = useQueryClient();
  return useMutation<PresetPublishResponse, Error, PublishPresetInput>({
    mutationFn: ({ id, ...body }) =>
      apiClient<PresetPublishResponse>(`${PRESETS_ENDPOINT}/${id}/publish`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (data) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: presetCardQueryKey(data.id) }),
        queryClient.invalidateQueries({ queryKey: presetsListQueryKey }),
      ]);
    },
  });
}

export interface ArchivePresetInput extends PresetArchiveRequest {
  id: string;
}

export function useArchivePresetMutation(): UseMutationResult<
  PresetPatchResponse,
  Error,
  ArchivePresetInput
> {
  const queryClient = useQueryClient();
  return useMutation<PresetPatchResponse, Error, ArchivePresetInput>({
    mutationFn: ({ id, ...body }) =>
      apiClient<PresetPatchResponse>(`${PRESETS_ENDPOINT}/${id}/archive`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (data) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: presetCardQueryKey(data.id) }),
        queryClient.invalidateQueries({ queryKey: presetsListQueryKey }),
      ]);
    },
  });
}

export interface UnarchivePresetInput {
  id: string;
}

/**
 * `POST /admin/presets/:id/unarchive` — «Вернуть из архива» (BUG-377): the
 * preset comes back as a draft. The body is empty (`presetUnarchiveRequestSchema`).
 */
export function useUnarchivePresetMutation(): UseMutationResult<
  PresetPatchResponse,
  Error,
  UnarchivePresetInput
> {
  const queryClient = useQueryClient();
  return useMutation<PresetPatchResponse, Error, UnarchivePresetInput>({
    mutationFn: ({ id }) => {
      const body: PresetUnarchiveRequest = {};
      return apiClient<PresetPatchResponse>(`${PRESETS_ENDPOINT}/${id}/unarchive`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    onSuccess: async (data) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: presetCardQueryKey(data.id) }),
        queryClient.invalidateQueries({ queryKey: presetsListQueryKey }),
      ]);
    },
    // A refusal means the card is stale (someone else returned or deleted the
    // preset): re-read it so the page stops offering the return.
    onError: async (_error, variables) => {
      await queryClient.invalidateQueries({ queryKey: presetCardQueryKey(variables.id) });
    },
  });
}

export interface DeletePresetInput {
  id: string;
  force: boolean;
}

/**
 * `DELETE /admin/presets/:id` — soft delete (BUG-156). `force` is sent in
 * the request BODY (mirrors `presetDeleteRequestSchema`) so the BE audit
 * interceptor records it. On 409 `PRESET_IN_USE` the error envelope's
 * `details` matches `presetInUseDetailsSchema` (counts + up to 5 venues).
 * On success the card cache is REMOVED (not invalidated — a refetch would
 * 404 on the tombstone) and the presets list is invalidated.
 */
export function useDeletePresetMutation(): UseMutationResult<
  void,
  Error,
  DeletePresetInput
> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, DeletePresetInput>({
    mutationFn: ({ id, force }) =>
      apiClient<void>(`${PRESETS_ENDPOINT}/${id}`, {
        method: 'DELETE',
        body: JSON.stringify({ force }),
      }),
    onSuccess: async (_data, variables) => {
      queryClient.removeQueries({
        queryKey: presetCardQueryKey(variables.id),
      });
      await queryClient.invalidateQueries({ queryKey: presetsListQueryKey });
    },
  });
}

export interface DuplicatePresetInput extends PresetDuplicateRequest {
  id: string;
}

export function useDuplicatePresetMutation(): UseMutationResult<
  PresetCreateResponse,
  Error,
  DuplicatePresetInput
> {
  const queryClient = useQueryClient();
  return useMutation<PresetCreateResponse, Error, DuplicatePresetInput>({
    mutationFn: ({ id, ...body }) =>
      apiClient<PresetCreateResponse>(`${PRESETS_ENDPOINT}/${id}/duplicate`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: presetsListQueryKey });
    },
  });
}

// ---------------------------------------------------------------------------
// Wave 2.20 FE — Excludes + Priority mutations.
//
// Endpoints (Wave 2.18 BE-1, all `@AdminOrAbove()`):
//   POST   /admin/presets/:id/excludes                      → { added, skipped }
//   DELETE /admin/presets/:id/excludes/:track_id            → 204
//   POST   /admin/presets/:id/priority-tracks               → { preset_id, track_id, weight_multiplier }
//   DELETE /admin/presets/:id/priority-tracks/:track_id     → 204
//
// Invalidation strategy (Wave 2.13 FE M-1): each mutation awaits BOTH the
// preset card and the live track-count refetch BEFORE the modal closes so the
// counters in the header / Filter panel reflect the change immediately.
// ---------------------------------------------------------------------------

export interface PresetExcludesAddInput extends PresetExcludesAdd {
  id: string;
}

export interface PresetExcludesAddResult {
  added: number;
  skipped: number;
}

export function usePresetExcludesAddMutation(): UseMutationResult<
  PresetExcludesAddResult,
  Error,
  PresetExcludesAddInput
> {
  const queryClient = useQueryClient();
  return useMutation<PresetExcludesAddResult, Error, PresetExcludesAddInput>({
    mutationFn: ({ id, ...body }) =>
      apiClient<PresetExcludesAddResult>(`${PRESETS_ENDPOINT}/${id}/excludes`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: presetCardQueryKey(variables.id),
        }),
        queryClient.invalidateQueries({
          queryKey: presetTrackCountQueryKey(variables.id),
        }),
        queryClient.invalidateQueries({
          queryKey: ['preset-excludes', variables.id],
        }),
        queryClient.invalidateQueries({
          queryKey: ['preset-tracks', variables.id],
        }),
      ]);
    },
  });
}

export interface PresetExcludeRemoveInput {
  id: string;
  trackId: string;
}

export function usePresetExcludeRemoveMutation(): UseMutationResult<
  void,
  Error,
  PresetExcludeRemoveInput
> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, PresetExcludeRemoveInput>({
    mutationFn: ({ id, trackId }) =>
      apiClient<void>(`${PRESETS_ENDPOINT}/${id}/excludes/${trackId}`, {
        method: 'DELETE',
      }),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: presetCardQueryKey(variables.id),
        }),
        queryClient.invalidateQueries({
          queryKey: presetTrackCountQueryKey(variables.id),
        }),
        queryClient.invalidateQueries({
          queryKey: ['preset-excludes', variables.id],
        }),
        queryClient.invalidateQueries({
          queryKey: ['preset-tracks', variables.id],
        }),
      ]);
    },
  });
}

export interface PresetPriorityAddInput extends PresetPriorityTrackAdd {
  id: string;
}

export interface PresetPriorityAddResult {
  preset_id: string;
  track_id: string;
  weight_multiplier: number;
}

export function usePresetPriorityAddMutation(): UseMutationResult<
  PresetPriorityAddResult,
  Error,
  PresetPriorityAddInput
> {
  const queryClient = useQueryClient();
  return useMutation<PresetPriorityAddResult, Error, PresetPriorityAddInput>({
    mutationFn: ({ id, ...body }) =>
      apiClient<PresetPriorityAddResult>(
        `${PRESETS_ENDPOINT}/${id}/priority-tracks`,
        {
          method: 'POST',
          body: JSON.stringify(body),
        },
      ),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: presetCardQueryKey(variables.id),
        }),
        queryClient.invalidateQueries({
          queryKey: presetTrackCountQueryKey(variables.id),
        }),
        queryClient.invalidateQueries({
          queryKey: ['preset-priority', variables.id],
        }),
        queryClient.invalidateQueries({
          queryKey: ['preset-tracks', variables.id],
        }),
      ]);
    },
  });
}

export interface PresetPriorityRemoveInput {
  id: string;
  trackId: string;
}

export function usePresetPriorityRemoveMutation(): UseMutationResult<
  void,
  Error,
  PresetPriorityRemoveInput
> {
  const queryClient = useQueryClient();
  return useMutation<void, Error, PresetPriorityRemoveInput>({
    mutationFn: ({ id, trackId }) =>
      apiClient<void>(`${PRESETS_ENDPOINT}/${id}/priority-tracks/${trackId}`, {
        method: 'DELETE',
      }),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: presetCardQueryKey(variables.id),
        }),
        queryClient.invalidateQueries({
          queryKey: presetTrackCountQueryKey(variables.id),
        }),
        queryClient.invalidateQueries({
          queryKey: ['preset-priority', variables.id],
        }),
        queryClient.invalidateQueries({
          queryKey: ['preset-tracks', variables.id],
        }),
      ]);
    },
  });
}

// ---------------------------------------------------------------------------
// Wave 2.21 FE — version rollback mutation.
//
// **Wire-contract gap:** Wave 2.19 BE-1 shipped `GET /admin/presets/:id/versions`
// (and the underlying immutable history table). The mutation route
// `POST /admin/presets/:id/rollback/:version_id` is part of TASK-063 which has
// NOT been merged onto main at the time of writing (no controller route, no
// service method). The hook is wired against the contracted URL so the FE
// surfaces a clean 404 + Alert today and starts working the moment the BE
// merges. The `RollbackVersionConfirmModal` opens a `TASK-066c-followup`
// pointer Alert before firing.
// ---------------------------------------------------------------------------

export interface RollbackPresetVersionInput {
  id: string;
  versionId: string;
}

// ---------------------------------------------------------------------------
// Wave 2.67 FE — preset drafts + per-id preview-count + tracks list
// (TASK-066a-followup + TASK-066b-followup). Endpoints from Wave 2.67 BE polish.
// ---------------------------------------------------------------------------

/**
 * `POST /admin/presets/:id/draft-version` — clones the preset's current_version
 * into an editable draft. Returns the draft row + flat `draft_version_id` alias.
 */
export function useCreatePresetDraftMutation(): UseMutationResult<
  PresetDraftCreateResponse,
  Error,
  string
> {
  return useMutation<PresetDraftCreateResponse, Error, string>({
    mutationFn: (presetId) =>
      apiClient<PresetDraftCreateResponse>(
        `${PRESETS_ENDPOINT}/${presetId}/draft-version`,
        { method: 'POST', body: JSON.stringify({}) },
      ),
  });
}

export interface PatchPresetDraftInput extends PresetDraftPatchRequest {
  draftId: string;
  /** Source preset id — used solely to invalidate `preset-card`/`track-count`
   *  on success. The endpoint itself is addressed by `draftId`. */
  presetId: string;
}

/**
 * `PATCH /admin/preset-drafts/:id` — mutate filter_rules / playback_settings on
 * a draft row. Invalidates the source preset's card + track-count so the next
 * publish reads fresh state.
 */
export function usePatchPresetDraftMutation(): UseMutationResult<
  PresetDraftPatchResponse,
  Error,
  PatchPresetDraftInput
> {
  const queryClient = useQueryClient();
  return useMutation<PresetDraftPatchResponse, Error, PatchPresetDraftInput>({
    mutationFn: ({ draftId, presetId: _presetId, ...patch }) =>
      apiClient<PresetDraftPatchResponse>(
        `/admin/preset-drafts/${draftId}`,
        { method: 'PATCH', body: JSON.stringify(patch) },
      ),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: presetCardQueryKey(variables.presetId),
        }),
        queryClient.invalidateQueries({
          queryKey: presetTrackCountQueryKey(variables.presetId),
        }),
        // The Tracks list previews the effective (draft) filter, so a draft
        // edit must refresh it too — otherwise the list stays stale until a
        // manual page reload (CEO QA 2026-06-16). Prefix key matches all
        // `['preset-tracks', id, query]` variants.
        queryClient.invalidateQueries({
          queryKey: ['preset-tracks', variables.presetId],
        }),
      ]);
    },
  });
}

/**
 * Query key for the per-preset ad-hoc preview-count (`POST /admin/presets/:id/
 * preview-count`). Includes the request body so distinct filters cache
 * independently.
 */
export const presetPreviewCountByIdQueryKey = (
  id: string,
  body: PresetPreviewCountByIdRequest,
) => ['preset-preview-count-by-id', id, body] as const;

/**
 * Per-preset live count for an ad-hoc filter (Wave 2.67 BE). Unlike
 * `usePresetPreviewCountQuery` (library-wide), this variant honours the
 * preset's `preset_excludes`. Caller must already debounce the filter input.
 */
export function usePresetPreviewCountByIdQuery(
  presetId: string | undefined,
  body: PresetPreviewCountByIdRequest | null,
  options?: { enabled?: boolean },
) {
  const enabled =
    (options?.enabled ?? true) && Boolean(presetId) && body !== null;
  return useQuery<PresetPreviewCountByIdResponse>({
    queryKey: presetPreviewCountByIdQueryKey(
      presetId ?? '',
      body ?? { filter_rules: {} },
    ),
    queryFn: () =>
      apiClient<PresetPreviewCountByIdResponse>(
        `${PRESETS_ENDPOINT}/${presetId}/preview-count`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    staleTime: STALE_TIME.liveCounter,
    enabled,
  });
}

export const presetTracksQueryKey = (
  id: string,
  query: PresetTracksListQuery,
) => ['preset-tracks', id, query] as const;

/**
 * `GET /admin/presets/:id/tracks?limit&offset` (Wave 2.67 BE).
 */
export function usePresetTracksQuery(
  id: string | undefined,
  query: PresetTracksListQuery,
  options?: { enabled?: boolean },
) {
  const enabled = (options?.enabled ?? true) && Boolean(id);
  return useQuery<PresetTracksListResponse>({
    queryKey: presetTracksQueryKey(id ?? '', query),
    queryFn: () => {
      const sp = new URLSearchParams();
      sp.set('limit', String(query.limit));
      sp.set('offset', String(query.offset));
      return apiClient<PresetTracksListResponse>(
        `${PRESETS_ENDPOINT}/${id}/tracks?${sp.toString()}`,
      );
    },
    staleTime: STALE_TIME.list,
    enabled,
    placeholderData: (prev) => prev,
  });
}

/**
 * `GET /admin/presets/:id/preview-sequence` — a batch of tracks in the order
 * the venue player would produce them (chained rotation scoring — Camelot /
 * mood / priority). Plain fetch, click-triggered from «Прослушать как в
 * эфире»; no react-query cache — every listen should build a fresh sequence.
 *
 * The rotation is endless: to keep listening past the first batch, pass the
 * last played track as `currentTrackId` (scoring context for the first pick)
 * and the played ids as `excludeTrackIds` (server caps at 200).
 */
export function fetchPresetPreviewSequence(
  id: string,
  opts: {
    seed?: number;
    count?: number;
    currentTrackId?: string;
    excludeTrackIds?: string[];
  } = {},
): Promise<PresetPreviewSequenceResponse> {
  const sp = new URLSearchParams();
  if (opts.seed !== undefined) sp.set('seed', String(opts.seed));
  if (opts.count !== undefined) sp.set('count', String(opts.count));
  if (opts.currentTrackId !== undefined) {
    sp.set('current_track_id', opts.currentTrackId);
  }
  if (opts.excludeTrackIds !== undefined && opts.excludeTrackIds.length > 0) {
    sp.set('exclude_track_ids', opts.excludeTrackIds.slice(-200).join(','));
  }
  const qs = sp.toString();
  return apiClient<PresetPreviewSequenceResponse>(
    `${PRESETS_ENDPOINT}/${id}/preview-sequence${qs ? `?${qs}` : ''}`,
  );
}

export function useRollbackPresetVersionMutation(): UseMutationResult<
  PresetPublishResponse,
  Error,
  RollbackPresetVersionInput
> {
  const queryClient = useQueryClient();
  return useMutation<PresetPublishResponse, Error, RollbackPresetVersionInput>({
    mutationFn: ({ id, versionId }) =>
      apiClient<PresetPublishResponse>(
        `${PRESETS_ENDPOINT}/${id}/rollback/${versionId}`,
        { method: 'POST' },
      ),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: presetCardQueryKey(variables.id),
        }),
        queryClient.invalidateQueries({
          queryKey: presetVersionsQueryKey(variables.id),
        }),
        queryClient.invalidateQueries({
          queryKey: presetTrackCountQueryKey(variables.id),
        }),
        queryClient.invalidateQueries({ queryKey: presetsListQueryKey }),
      ]);
    },
  });
}

// ---------------------------------------------------------------------------
// Wave 2.68 FE — preset excludes + priority list endpoints.
//
// Endpoints (Wave 2.68 BE, in flight in a parallel worktree):
//   GET /admin/presets/:id/excludes?limit&offset         → PresetExcludesListResponse
//   GET /admin/presets/:id/priority-tracks?limit&offset  → PresetPriorityListResponse
//
// PROVISIONAL TYPES: `@music-app/shared` has not yet re-exported
// `presetExcludesListResponseSchema` / `presetPriorityListResponseSchema` at
// the moment this hook lands. Until that ships, we use the local types below.
// They mirror the `presetTracksListResponseSchema` envelope from Wave 2.67 BE
// (`{ items, total, limit, offset }`) plus the per-row fields the BE brief
// committed to (title / artist / bpm / camelot_key / library_type +
// added_at + reason for excludes / weight_multiplier for priority).
//
// Canonical types `PresetExcludesListItem` / `PresetExcludesListResponse` /
// `PresetPriorityListItem` / `PresetPriorityListResponse` are imported from
// `@music-app/shared` (Wave 2.68 BE Goal 4). Local provisional copies removed
// at integration time.
// ---------------------------------------------------------------------------

export interface PresetListPaginationQuery {
  limit: number;
  offset: number;
}

export const presetExcludesQueryKey = (
  id: string,
  query: PresetListPaginationQuery,
) => ['preset-excludes', id, query] as const;

export const presetPriorityQueryKey = (
  id: string,
  query: PresetListPaginationQuery,
) => ['preset-priority', id, query] as const;

export function usePresetExcludesQuery(
  id: string | undefined,
  query: PresetListPaginationQuery,
  options?: { enabled?: boolean },
) {
  const enabled = (options?.enabled ?? true) && Boolean(id);
  return useQuery<PresetExcludesListResponse>({
    queryKey: presetExcludesQueryKey(id ?? '', query),
    queryFn: () => {
      const sp = new URLSearchParams();
      sp.set('limit', String(query.limit));
      sp.set('offset', String(query.offset));
      return apiClient<PresetExcludesListResponse>(
        `${PRESETS_ENDPOINT}/${id}/excludes?${sp.toString()}`,
      );
    },
    staleTime: STALE_TIME.list,
    enabled,
    placeholderData: (prev) => prev,
  });
}

export function usePresetPriorityQuery(
  id: string | undefined,
  query: PresetListPaginationQuery,
  options?: { enabled?: boolean },
) {
  const enabled = (options?.enabled ?? true) && Boolean(id);
  return useQuery<PresetPriorityListResponse>({
    queryKey: presetPriorityQueryKey(id ?? '', query),
    queryFn: () => {
      const sp = new URLSearchParams();
      sp.set('limit', String(query.limit));
      sp.set('offset', String(query.offset));
      return apiClient<PresetPriorityListResponse>(
        `${PRESETS_ENDPOINT}/${id}/priority-tracks?${sp.toString()}`,
      );
    },
    staleTime: STALE_TIME.list,
    enabled,
    placeholderData: (prev) => prev,
  });
}
